import React, { createContext, useCallback, useContext, useState } from "react";

import { Modal, ModalProps } from "components";

type ModalContent = {
  title: string;
  message: string;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
};

type ModalContextType = {
  showModal: (content: ModalContent) => void;
  hideModal: () => void;
  modalProps: ModalProps | null;
};

const ModalContext = createContext<ModalContextType>({
  showModal: () => {},
  hideModal: () => {},
  modalProps: null,
});

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);

  const hideModal = useCallback(() => {
    setModalProps(null);
  }, []);

  const showModal = useCallback(
    (content: ModalContent) => {
      setModalProps({
        showModal: true,
        onOk: content.onConfirm,
        onCancel: hideModal,
        okText: content.confirmText,
        cancelText: content.cancelText,
        title: content.title,
        content: content.message,
        zIndex: 9999,
        destroyOnHidden: true,
      });
    },
    [hideModal]
  );

  return (
    <ModalContext.Provider value={{ showModal, hideModal, modalProps }}>
      {children}
      {modalProps && <Modal {...modalProps} />}
    </ModalContext.Provider>
  );
};

export const useTimelineModal = () => useContext(ModalContext);
